import { Box, Button, Center, Text } from "@chakra-ui/react";
import { FaRegCompass } from "react-icons/fa";
import { useInfiniteQuery, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { readingtitleMapIndex } from "../../../core/constants";
import { getGoals } from "../../../services/goalsApi";
import { getReadings } from "../../../services/readingOrdersApi";
import {
  findLiveDeadlineGoal,
  readingGoalLabel,
} from "../../profile/helpers/goals";
import Spinner from "../../../shared/components/Spinner";
import CompletedReadingOrdersCard from "./CompletedReadingOrdersCard";
import ReadingCard from "./ReadingCard";

const compareReadings = (a, b) => {
  const aTitle = a?.readingOrder?.title || "";
  const bTitle = b?.readingOrder?.title || "";

  if (aTitle < bTitle) {
    return -1;
  }
  if (aTitle > bTitle) {
    return 1;
  }
  return 0;
};

const ReadingList = ({ franchise, completed = 0 }) => {
  const navigate = useNavigate();
  const { isLoading, data, isSuccess } = useInfiniteQuery(
    ["readings", franchise, completed],
    ({ pageParam = 1 }) => getReadings(franchise, pageParam, completed),
    {
      getNextPageParam: (pageParam, allPages) => {
        if (pageParam?.meta?.currentPage === pageParam?.meta?.lastPage) {
          return undefined;
        }
        return pageParam?.meta?.currentPage + 1;
      },
    }
  );
  // Goal progress shown inline on orders that have one.
  const goalsQuery = useQuery(["goals"], getGoals, {
    staleTime: 5 * 60 * 1000,
  });
  const liveGoals = Array.isArray(goalsQuery.data?.data?.active)
    ? goalsQuery.data.data.active
    : [];
  let queryData = [];

  if (isLoading) {
    return <Spinner />;
  }

  if (!isLoading && isSuccess) {
    queryData = data.pages
      .flatMap((group) => group?.data || [])
      .filter((reading) => reading?.readingOrder?.type != null)
      .sort(compareReadings)
      .reduce((acc, item) => {
        const key = parseInt(item.readingOrder.type, 10);
        if (!acc[key - 1]) {
          acc[key - 1] = { title: key, data: [] };
        }
        acc[key - 1].data.push(item);
        return acc;
      }, [])
      .filter((x) => x);
  }

  const hasCompletedReadingOrders =
    Number(completed) !== 1 &&
    data?.pages?.some((page) => page?.meta?.hasCompletedReadingOrders === true);

  return (
    <div>
      {queryData.map((group, index) => (
        <Box key={index}>
          <Text fontSize="sm" color="gray.400" mt="6" mb="2" casing="uppercase">
            {readingtitleMapIndex[group.title]}
          </Text>
          {group.data.map((reading) => (
            <ReadingCard
              key={reading.id}
              url={`/reading/${reading.id}`}
              reading={reading}
              progress={reading?.progress}
              goalLabel={readingGoalLabel(
                findLiveDeadlineGoal(liveGoals, reading?.readingOrder?.id)
              )}
            />
          ))}
        </Box>
      ))}
      {queryData.length === 0 && (
        <Center p="30" flexDirection="column">
          <FaRegCompass size="60px" color="#666" />
          <Text my="3">You have no reading order in this section.</Text>
          <Button
            size="sm"
            variant="outline"
            colorScheme="red"
            onClick={() => navigate("/browse")}
          >
            Browse reading orders
          </Button>
        </Center>
      )}
      {queryData.length > 0 && hasCompletedReadingOrders && (
        <CompletedReadingOrdersCard franchise={franchise} />
      )}
    </div>
  );
};

export default ReadingList;
